import React, { useState, useEffect } from 'react'
import ScrollReveal from '../components/marketing/ScrollReveal'
import { setTheme } from '../lib/theme'

// ─── Responsive breakpoint hook ───
function useBreakpoint() {
  const [w, setW] = useState(typeof window !== 'undefined' ? window.innerWidth : 1440)
  useEffect(() => {
    const onResize = () => setW(window.innerWidth)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])
  return { mobile: w < 640, tablet: w < 1024, w }
}

// ─── Design tokens (CSS-variable-driven for light/dark) ───
const t = {
  bg: 'var(--lp-bg)',
  bgDark: '#252019',
  heroBg: '#1A1714',
  socialBg: 'var(--lp-social-bg)',
  border: 'var(--lp-border)',
  text: 'var(--lp-text)',
  textMuted: 'var(--lp-text-muted)',
  textBody: 'var(--lp-text-body)',
  textFaint: 'var(--lp-text-faint)',
  textFaintAlt: 'var(--lp-text-faint-alt)',
  accent: 'var(--lp-accent)',
  accentBtn: 'var(--lp-accent-btn)',
  badgeBg: 'var(--lp-badge-bg)',
  cardBg: 'var(--lp-card-bg)',
  fontMain: '"Geist", system-ui, sans-serif',
} as const

// ─── Reusable: Corner Logo SVG ───
function CornerLogo({ color = t.text, size = 22, gradId = 'navLogoGrad' }: { color?: string; size?: number; gradId?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
      <svg width={size} height={size} viewBox="0 0 256 256" fill="none">
        <defs>
          <linearGradient id={gradId} x1="42" y1="38.5" x2="207" y2="209" gradientUnits="userSpaceOnUse">
            <stop stopColor="#D46A3A"/>
            <stop offset="1" stopColor="#8B3A10"/>
          </linearGradient>
        </defs>
        <path d="M236 152V32C236 25.373 230.627 20 224 20H32C25.373 20 20 25.373 20 32L20 224C20 230.627 25.373 236 32 236H152" stroke={`url(#${gradId})`} strokeWidth="40" strokeLinecap="round" />
      </svg>
      <span style={{ fontSize: size === 22 ? 16 : 15, fontWeight: 600, color, fontFamily: t.fontMain, letterSpacing: '-0.01em', lineHeight: size === 22 ? '20px' : '18px' }}>
        Corner
      </span>
    </div>
  )
}

// ─── Reusable: Section Badge ───
function SectionBadge({ children }: { children: string }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      borderRadius: 100, padding: '6px 14px',
      backgroundColor: t.badgeBg,
      fontSize: 11, fontWeight: 500, letterSpacing: '0.12em',
      textTransform: 'uppercase' as const, color: t.accent,
      fontFamily: t.fontMain, lineHeight: '14px',
    }}>
      {children}
    </span>
  )
}

// ─── Reusable: Check Item ───
function CheckItem({ children }: { children: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
        <path d="M2.5 7.5l3 3 6-6" stroke={t.accent} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <span style={{ fontSize: 15, color: t.textMuted, fontFamily: t.fontMain, lineHeight: '18px' }}>{children}</span>
    </div>
  )
}

// ─── Reusable: Feature Label ───
function FeatureLabel({ children }: { children: string }) {
  return (
    <div style={{
      display: 'inline-flex', width: 'fit-content',
      borderRadius: 100, padding: '6px 14px',
      backgroundColor: t.badgeBg,
    }}>
      <span style={{ fontSize: 11, fontWeight: 500, letterSpacing: '0.1em', textTransform: 'uppercase' as const, color: t.accent, fontFamily: t.fontMain, lineHeight: '14px' }}>
        {children}
      </span>
    </div>
  )
}

// ═══════════════════════════════════════════════
// NAVBAR
// ═══════════════════════════════════════════════
function Navbar() {
  const { mobile, tablet } = useBreakpoint()
  return (
    <nav style={{
      width: '100%', height: 60,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: mobile ? '0 16px' : '0 40px',
      backgroundColor: 'var(--lp-navbar-bg)',
      borderBottom: `1px solid ${t.border}`,
      fontFamily: t.fontMain,
      position: 'sticky', top: 0, zIndex: 100,
      backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
    }}>
      <a href="/" style={{ textDecoration: 'none' }}>
        <CornerLogo />
      </a>

      {!tablet && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 32 }}>
          {['Features', 'How it works', 'Pricing', 'FAQ'].map(link => (
            <a key={link} href={link === 'FAQ' ? '#faq' : link === 'Pricing' ? '/pricing' : link === 'Features' ? '/features' : link === 'How it works' ? '/how-it-works' : '#'} style={{
              fontSize: 14, fontWeight: 450, color: t.textMuted,
              textDecoration: 'none', lineHeight: '18px',
            }}>{link}</a>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: mobile ? 10 : 20 }}>
        {!mobile && (
          <a href="/auth/login" style={{ fontSize: 14, color: t.textMuted, textDecoration: 'none', lineHeight: '18px' }}>Sign in</a>
        )}
        <a href="/auth/register" style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          borderRadius: 8, padding: mobile ? '8px 12px' : '9px 18px',
          backgroundColor: t.accent, color: '#fff',
          fontSize: mobile ? 13 : 14, fontWeight: 500, textDecoration: 'none', lineHeight: '20px',
        }}>Get started →</a>
      </div>
    </nav>
  )
}

// ═══════════════════════════════════════════════
// HERO
// ═══════════════════════════════════════════════
function Hero() {
  const { mobile } = useBreakpoint()
  return (
    <section style={{
      width: '100%',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      padding: mobile ? '60px 20px 40px' : '80px 170px 0',
    }}>
      {/* Badge row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 32, justifyContent: 'center', flexWrap: 'wrap' }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center',
          borderRadius: 100, padding: '4px 12px',
          backgroundColor: t.badgeBg,
          fontSize: 11, fontWeight: 600, letterSpacing: '0.08em',
          textTransform: 'uppercase' as const, color: t.accent,
          lineHeight: '14px',
        }}>✦ Backed by Alchemist Accelerator</span>
        <span style={{ fontSize: 12, color: t.textBody, fontWeight: 500, letterSpacing: '0.02em', lineHeight: '16px' }}>·</span>
        <span style={{ fontSize: 12, color: t.textFaint, fontWeight: 500, letterSpacing: '0.02em', lineHeight: '16px' }}>Your one tab for documents</span>
      </div>

      {/* Headline */}
      <div style={{ marginBottom: 20, textAlign: 'center' }}>
        <h1 style={{ fontSize: mobile ? 36 : 62, fontWeight: 600, letterSpacing: '-0.03em', lineHeight: '108%', color: t.text, fontFamily: t.fontMain, margin: 0 }}>
          Stop managing documents.
        </h1>
        <h1 style={{ fontSize: mobile ? 36 : 62, fontWeight: 600, letterSpacing: '-0.03em', lineHeight: '108%', color: t.accent, fontFamily: t.fontMain, margin: 0 }}>
          Start finishing them.
        </h1>
      </div>

      {/* Subtitle */}
      <p style={{ fontSize: 15, textAlign: 'center', lineHeight: '22px', letterSpacing: '0.02em', color: t.textFaint, marginBottom: 16, maxWidth: 600 }}>
        For real estate agents, founders, and operators who close deals with documents.
      </p>

      {/* Body */}
      <p style={{ fontSize: 18, textAlign: 'center', lineHeight: '30px', color: t.textBody, marginBottom: 32, maxWidth: 600 }}>
        Upload a contract, NDA, or lease — Corner reads it, flags what matters, and lets your team review and sign together. No more juggling tools.
      </p>

      {/* CTA Buttons */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 56, flexWrap: 'wrap', justifyContent: 'center' }}>
        <a href="/auth/register" style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          borderRadius: 8, padding: '12px 24px',
          backgroundColor: t.accentBtn, color: '#fff',
          fontSize: 15, fontWeight: 500, textDecoration: 'none', lineHeight: '18px',
        }}>Get started free →</a>
        <a href="#how-it-works" style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          borderRadius: 8, padding: '11px 24px',
          border: `1px solid ${t.border}`, color: t.text,
          fontSize: 15, textDecoration: 'none', lineHeight: '18px',
          backgroundColor: 'transparent',
        }}>See how it works</a>
      </div>
    </section>
  )
}

// ═══════════════════════════════════════════════
// HERO MOCKUP (placeholder for screenshot)
// ═══════════════════════════════════════════════
function HeroMockup() {
  const { mobile } = useBreakpoint()
  // Image is 2880×1520 (2x of 1440×760 section). Card is 1100×680 centered with 80px side padding, no top padding.
  // Card starts at x=170, y=0 in the section (margin auto = (1440-1100)/2 = 170). Bottom padding = 80.
  return (
    <section style={{
      width: '100%',
      display: 'flex', justifyContent: 'center',
      padding: mobile ? '0 18px 48px' : '0 80px 80px',
    }}>
      <div style={{
        width: '100%',
        maxWidth: mobile ? 420 : 1100,
        height: mobile ? 'auto' : 680,
        aspectRatio: mobile ? '1.62 / 1' : undefined,
        borderRadius: mobile ? 10 : 14, overflow: 'hidden',
        backgroundColor: t.cardBg,
        border: `1px solid ${t.border}`,
        boxShadow: mobile ? '0 8px 28px var(--lp-shadow-light)' : '0 4px 60px var(--lp-shadow-card), 0 1px 4px var(--lp-shadow-light)',
        backgroundImage: 'url(/Landingpage/HerosScreens/HeroMockup@2x.png)',
        backgroundSize: mobile ? 'cover' : '1440px 760px',
        backgroundPosition: mobile ? 'center top' : '-170px 0px',
        backgroundRepeat: 'no-repeat',
      }} />
    </section>
  )
}

// ═══════════════════════════════════════════════
// SOCIAL PROOF
// ═══════════════════════════════════════════════
function SocialProof() {
  const { mobile } = useBreakpoint()
  const orgs = ['Harvard', 'Stanford', 'CMU', 'Cornell', 'Google', 'Microsoft', 'SpaceX', 'Tesla', 'Y Combinator', 'Alchemist Accelerator', 'ODF']
  const roles = ['Independent Professionals', 'Real Estate Teams', 'Founders & Operators', 'Law Firms', 'Medical Offices']

  const orgNameStyle = { fontSize: 14, fontWeight: 600, color: t.textFaintAlt, opacity: 0.55, lineHeight: '18px', whiteSpace: 'nowrap' as const, flexShrink: 0 }
  const dotStyle = { fontSize: 12, color: 'var(--lp-dot)', lineHeight: '16px', flexShrink: 0 }

  const renderOrgSet = (keyPrefix: string) => (
    <>
      {orgs.map((org, i) => (
        <React.Fragment key={`${keyPrefix}-${org}`}>
          {i > 0 && <span style={dotStyle}>·</span>}
          <span style={orgNameStyle}>{org}</span>
        </React.Fragment>
      ))}
    </>
  )

  return (
    <section style={{
      width: '100%',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      padding: mobile ? '24px 20px' : '28px 40px', gap: 14,
      backgroundColor: t.socialBg,
      borderTop: `1px solid ${t.border}`, borderBottom: `1px solid ${t.border}`,
    }}>
      <style>{`
        @keyframes scroll-orgs {
          0% { transform: translateX(0); }
          100% { transform: translateX(-25%); }
        }
      `}</style>

      {/* Org names row — 229.5px inset on each side, carousel fills remaining space */}
      <div style={{
        display: 'flex',
        alignItems: mobile ? 'stretch' : 'center',
        flexDirection: mobile ? 'column' : 'row',
        gap: mobile ? 12 : 28,
        width: '100%',
        padding: mobile ? 0 : '0 229.5px',
        overflow: 'hidden',
      }}>
        <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' as const, color: t.textFaintAlt, lineHeight: '14px', flexShrink: 0, textAlign: mobile ? 'center' : 'left' }}>Trusted by people at</span>
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 28,
            width: 'max-content',
            animation: 'scroll-orgs 25s linear infinite',
          }}>
            {renderOrgSet('a')}
            <span style={dotStyle}>·</span>
            {renderOrgSet('b')}
            <span style={dotStyle}>·</span>
            {renderOrgSet('c')}
            <span style={dotStyle}>·</span>
            {renderOrgSet('d')}
            <span style={dotStyle}>·</span>
          </div>
        </div>
      </div>

      {/* Roles row */}
      {!mobile && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
          <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' as const, color: t.textFaintAlt, lineHeight: '14px', marginRight: 6 }}>Used by</span>
          {roles.map(role => (
            <span key={role} style={{
              borderRadius: 100, padding: '4px 12px',
              backgroundColor: t.bg, border: `1px solid ${t.border}`,
              fontSize: 12, color: t.textMuted, lineHeight: '16px',
            }}>{role}</span>
          ))}
        </div>
      )}
    </section>
  )
}

// ═══════════════════════════════════════════════
// HOW IT WORKS
// ═══════════════════════════════════════════════
function HowItWorks() {
  const { mobile } = useBreakpoint()
  return (
    <section id="how-it-works" style={{
      width: '100%',
      padding: mobile ? '64px 20px' : '100px 170px 64px',
      backgroundColor: t.bg,
    }}>
      {/* Section header */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', marginBottom: 56, gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <SectionBadge>How it works</SectionBadge>
          <a href="/how-it-works" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: '50%', backgroundColor: 'color-mix(in srgb, var(--lp-accent) 8%, transparent)', textDecoration: 'none', flexShrink: 0 }} title="See full page">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={t.accent} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
          </a>
        </div>
        <h2 style={{ fontSize: mobile ? 28 : 44, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: mobile ? '115%' : '52px', textAlign: 'center', color: t.text, fontFamily: t.fontMain, margin: 0 }}>
          The old way broke. This is the fix.
        </h2>
      </div>

      {/* Before / After cards */}
      <div style={{ display: 'flex', gap: 40, maxWidth: 1200, margin: '0 auto', flexDirection: mobile ? 'column' : 'row' }}>
        {/* Without Corner */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: '#D94545' }} />
            <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' as const, color: t.textFaint, lineHeight: '16px' }}>Without Corner</span>
          </div>
          <div style={{
            width: mobile ? '100%' : 520,
            height: mobile ? 'auto' : 320,
            aspectRatio: mobile ? '13 / 8' : undefined,
            borderRadius: mobile ? 12 : 16,
            overflow: 'hidden', backgroundColor: 'var(--lp-card-subtle)',
            border: `1px solid ${t.border}`,
          }}>
            <img src="/Landingpage/HerosScreens/ToolChaos@2x.png" alt="Without Corner" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 30%', display: 'block' }} />
          </div>
          <span style={{ fontSize: 13, fontWeight: 500, color: t.textFaint, lineHeight: '16px' }}>4+ tools · 45 minutes per document</span>
        </div>

        {/* With Corner */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: t.accentBtn }} />
            <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' as const, color: t.accentBtn, lineHeight: '16px' }}>With Corner</span>
          </div>
          <div style={{
            width: mobile ? '100%' : 520,
            height: mobile ? 'auto' : 320,
            aspectRatio: mobile ? '13 / 8' : undefined,
            borderRadius: mobile ? 12 : 16,
            overflow: 'hidden', backgroundColor: '#2E2318',
          }}>
            <img src="/Landingpage/HerosScreens/CornerFlow@2x.png" alt="With Corner" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          </div>
          <span style={{ fontSize: 13, fontWeight: 600, color: t.accentBtn, lineHeight: '16px' }}>1 tool · under 5 minutes</span>
        </div>
      </div>
    </section>
  )
}

// ═══════════════════════════════════════════════
// WORKFLOW CARDS
// ═══════════════════════════════════════════════
function WorkflowCards() {
  const { mobile } = useBreakpoint()
  const cards = [
    { img: '/Landingpage/HerosScreens/RealEstateFlow.png', alt: 'Real estate workflow' },
    { img: '/Landingpage/HerosScreens/ContractsFlow.png', alt: 'Contracts workflow' },
    { img: '/Landingpage/HerosScreens/OperationsFlow.png', alt: 'Operations workflow' },
    { img: '/Landingpage/HerosScreens/ResearchFlow.png', alt: 'Research workflow' },
  ]

  return (
    <section style={{
      width: '100%',
      padding: mobile ? '64px 20px' : '100px 170px',
      backgroundColor: t.bg,
    }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 48 }}>
        {/* Header */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
          <SectionBadge>Use cases</SectionBadge>
          <h2 style={{ fontSize: mobile ? 28 : 40, fontWeight: 600, letterSpacing: '-0.02em', lineHeight: '115%', textAlign: 'center', color: t.text, fontFamily: t.fontMain, margin: 0, maxWidth: 740 }}>
            Whatever your work looks like —{'\n'}Corner handles the document side of it.
          </h2>
        </div>

        {/* Cards grid */}
        <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : '1fr 1fr', gap: 20 }}>
          {cards.map(card => (
            <div key={card.alt} style={{
              borderRadius: 12, overflow: 'hidden',
              maxWidth: mobile ? 340 : undefined,
              justifySelf: mobile ? 'center' : 'stretch',
            }}>
              <img src={card.img} alt={card.alt} style={{ width: '100%', display: 'block' }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

// ═══════════════════════════════════════════════
// FEATURE: AI ANALYSIS
// ═══════════════════════════════════════════════
function FeatureAIAnalysis() {
  const { mobile } = useBreakpoint()
  return (
    <section style={{
      width: '100%', maxWidth: 1440, height: mobile ? 'auto' : 620,
      position: 'relative', display: 'flex', alignItems: 'center',
      overflow: 'hidden', margin: '0 auto',
    }}>
      {/* Left text */}
      <div style={{
        width: mobile ? '100%' : 440, flexShrink: 0,
        display: 'flex', flexDirection: 'column',
        padding: mobile ? '64px 20px 40px' : '0 40px 0 120px', gap: 24,
        position: 'relative', zIndex: 1,
      }}>
        <FeatureLabel>AI document analysis</FeatureLabel>
        <h2 style={{ fontSize: mobile ? 30 : 40, fontWeight: 700, letterSpacing: '-0.03em', lineHeight: '108%', color: t.text, fontFamily: t.fontMain, margin: 0 }}>
          Know what{'\n'}you're signing.{'\n'}Before you sign it.
        </h2>
        <p style={{ fontSize: 16, lineHeight: '165%', color: t.textMuted, margin: 0, maxWidth: 280 }}>
          Upload any document and have a real conversation with it. Ask Corner to summarize, find the risky clauses, pull every key term, or explain what you're actually agreeing to — in plain English.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 390 }}>
          <CheckItem>Plain-English summary instantly</CheckItem>
          <CheckItem>Risky clauses flagged before you sign</CheckItem>
          <CheckItem>Ask follow-ups — Corner keeps full context</CheckItem>
        </div>
      </div>

      {/* Right mockup — absolutely positioned, bleeds off right edge */}
      {!mobile && (
        <div style={{
          position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)',
          width: 940, height: 532,
          borderTopLeftRadius: 14, borderBottomLeftRadius: 14,
          overflow: 'hidden',
          backgroundColor: t.cardBg,
          borderTop: `1px solid ${t.border}`,
          borderLeft: `1px solid ${t.border}`,
          borderBottom: `1px solid ${t.border}`,
          boxShadow: '-6px 4px 48px var(--lp-shadow-light)',
          backgroundImage: 'url(/Landingpage/HerosScreens/FeatureAIAnalysis@2x.png)',
          backgroundSize: '1440px 620px',
          backgroundPosition: '-500px -44px',
          backgroundRepeat: 'no-repeat',
        }} />
      )}
      {mobile && (
        <div style={{ padding: '0 20px 64px', width: '100%', display: 'flex', justifyContent: 'center' }}>
          <img
            src="/Landingpage/HerosScreens/FeatureAIAnalysis@2x.png"
            alt="AI document analysis in Corner"
            style={{ width: '100%', maxWidth: 360, borderRadius: 12, boxShadow: '0 8px 32px var(--lp-shadow-light)', display: 'block' }}
          />
        </div>
      )}
    </section>
  )
}

// ═══════════════════════════════════════════════
// FEATURE: DOCUMENT GENERATION
// ═══════════════════════════════════════════════
function FeatureDocGen() {
  const { mobile } = useBreakpoint()
  return (
    <section style={{
      width: '100%', height: mobile ? 'auto' : 580,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        display: 'flex', flexDirection: mobile ? 'column-reverse' : 'row-reverse',
        alignItems: mobile ? 'stretch' : 'center', gap: mobile ? 40 : 80,
        maxWidth: 1100, width: '100%', padding: mobile ? '64px 20px' : '0',
      }}>
        {/* Text side (right in desktop via row-reverse) */}
        <div style={{
          width: mobile ? '100%' : 460, flexShrink: 0,
          display: 'flex', flexDirection: 'column', gap: 28,
        }}>
          {/* Label + Heading group */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <span style={{ fontSize: 11, fontWeight: 500, letterSpacing: '0.12em', textTransform: 'uppercase' as const, color: t.accent, fontFamily: t.fontMain, lineHeight: '14px' }}>
              Document generation
            </span>
            <h2 style={{ fontSize: mobile ? 30 : 38, fontWeight: 700, letterSpacing: '-0.03em', lineHeight: '110%', color: t.text, fontFamily: t.fontMain, margin: 0 }}>
              Draft from scratch.{'\n'}Already state-compliant.
            </h2>
          </div>
          <p style={{ fontSize: 16, lineHeight: '165%', color: t.textMuted, margin: 0 }}>
            Describe what you need. Corner drafts the full document — adjusted for your state, your situation, and what's actually enforceable where you are. No blank page. No legal guesswork.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {['NDAs, leases, SOWs, invoices generated in seconds', 'State-specific clauses that are actually enforceable where you work', 'Edit, sign, and send without leaving Corner'].map(text => (
              <div key={text} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, marginTop: 3 }}>
                  <circle cx="8" cy="8" r="7.5" stroke={t.accent} />
                  <path d="M5 8.5l2 2 4-4" stroke={t.accent} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span style={{ fontSize: 15, lineHeight: '150%', color: t.textMuted, fontFamily: t.fontMain }}>{text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Mockup side (left in desktop via row-reverse) */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: mobile ? 12 : 16, alignItems: mobile ? 'center' : 'stretch' }}>
          <div style={{ borderRadius: 12, overflow: 'hidden' }}>
            <img src="/Landingpage/HerosScreens/YourPrompt.png" alt="Your prompt" style={{ width: '100%', maxWidth: mobile ? 340 : undefined, display: 'block' }} />
          </div>
          <div style={{ borderRadius: 12, overflow: 'hidden' }}>
            <img src="/Landingpage/HerosScreens/NDASoftwareContractor.png" alt="NDA Software Contractor" style={{ width: '100%', maxWidth: mobile ? 340 : undefined, display: 'block' }} />
          </div>
        </div>
      </div>
    </section>
  )
}

// ═══════════════════════════════════════════════
// REAL-TIME COLLABORATION
// ═══════════════════════════════════════════════
function FeatureCollaboration() {
  const { mobile } = useBreakpoint()
  return (
    <section style={{
      width: '100%', maxWidth: 1440, height: mobile ? 'auto' : 620,
      position: 'relative', display: 'flex', alignItems: 'center',
      overflow: 'hidden', margin: '0 auto',
    }}>
      {/* Left text */}
      <div style={{
        width: mobile ? '100%' : 440, flexShrink: 0,
        display: 'flex', flexDirection: 'column',
        padding: mobile ? '64px 20px 40px' : '0 40px 0 120px', gap: 24,
        position: 'relative', zIndex: 1,
      }}>
        <FeatureLabel>Real-time collaboration</FeatureLabel>
        <h2 style={{ fontSize: mobile ? 30 : 40, fontWeight: 700, letterSpacing: '-0.03em', lineHeight: '108%', color: t.text, fontFamily: t.fontMain, margin: 0 }}>
          Work together.{'\n'}Decide faster.
        </h2>
        <p style={{ fontSize: 16, lineHeight: '165%', color: t.textMuted, margin: 0, maxWidth: 320 }}>
          Your team reviews the same document, in the same place, at the same time. See who's looking at what, discuss flagged clauses in context, and make decisions without switching to email or Slack.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 390 }}>
          <CheckItem>Live cursors and presence — see where teammates are</CheckItem>
          <CheckItem>Shared AI chat — everyone contributes to the analysis</CheckItem>
          <CheckItem>Share with a link — no accounts needed to view</CheckItem>
        </div>
      </div>

      {/* Right mockup — large, bleeds off right edge like AI Analysis */}
      {!mobile && (
        <div style={{
          position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)',
          width: 940, height: 532,
          borderTopLeftRadius: 14, borderBottomLeftRadius: 14,
          overflow: 'hidden',
          backgroundColor: t.cardBg,
          borderTop: `1px solid ${t.border}`,
          borderLeft: `1px solid ${t.border}`,
          borderBottom: `1px solid ${t.border}`,
          boxShadow: '-6px 4px 48px var(--lp-shadow-light)',
          backgroundImage: 'url(/Landingpage/17aCollaborationPresenceCursors@2x.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'left center',
          backgroundRepeat: 'no-repeat',
        }} />
      )}

      {/* Mobile fallback image */}
      {mobile && (
        <div style={{ padding: '0 20px 64px', width: '100%', display: 'flex', justifyContent: 'center' }}>
          <img
            src="/Landingpage/17aCollaborationPresenceCursors@2x.png"
            alt="Real-time collaboration in Corner"
            style={{ width: '100%', maxWidth: 360, borderRadius: 12, boxShadow: '0 8px 40px rgba(0,0,0,0.12)', display: 'block' }}
          />
        </div>
      )}
    </section>
  )
}

// ═══════════════════════════════════════════════
// REDDIT PAIN
// ═══════════════════════════════════════════════
function RedditPain() {
  const { mobile } = useBreakpoint()
  const cards = [
    { sub: 'r/freelance', votes: '2.4k', quote: '"I\'m paying for DocuSign. And a PDF editor. And I still manually copy the same data between them. Every. Single. Time."' },
    { sub: 'r/realestate', votes: '1.8k', quote: '"Every deal has 10 forms with the same buyer name — I type it 10 times, every single deal. There has to be a better way."' },
    { sub: 'r/legaladvice', votes: '3.1k', quote: '"Client sent a scanned PDF of a 30-page contract. Now I\'m manually re-typing the whole thing because my tools can\'t handle it."' },
    { sub: 'r/productivity', votes: '987', quote: '"I need to review a 40-page agreement by tomorrow morning and have no idea where to even start. Any tools that actually help?"' },
  ]

  return (
    <section style={{
      width: '100%', height: mobile ? 'auto' : 720,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{ maxWidth: 1100, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 52, padding: mobile ? '64px 20px' : '0' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 11, fontWeight: 500, letterSpacing: '0.12em', textTransform: 'uppercase' as const, color: t.accent, fontFamily: t.fontMain, lineHeight: '14px', textAlign: 'center' }}>
            Sound familiar?
          </span>
          <h2 style={{ fontSize: mobile ? 28 : 42, fontWeight: 700, letterSpacing: '-0.03em', lineHeight: '110%', textAlign: 'center', color: t.text, fontFamily: t.fontMain, margin: 0 }}>
            Real complaints.{'\n'}From real people.
          </h2>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, width: '100%' }}>
          {cards.map(card => (
            <div key={card.sub} style={{
              flex: 1, minWidth: mobile ? '100%' : 480,
              display: 'flex', flexDirection: 'column',
              borderRadius: 12, padding: '24px 28px', gap: 14,
              backgroundColor: t.cardBg, border: `1px solid ${t.border}`,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" style={{ flexShrink: 0 }}>
                  <circle cx="9" cy="9" r="9" fill="#FF4500" />
                  <path d="M14.5 9c0-.6-.5-1.1-1.1-1.1-.3 0-.6.1-.8.3-.8-.6-1.9-.9-3.1-1l.5-2.5 1.7.4c0 .5.4.9.9.9s.9-.4.9-.9-.4-.9-.9-.9c-.4 0-.7.2-.8.5l-1.9-.4c-.1 0-.2 0-.2.1l-.6 2.8c-1.2.1-2.3.4-3.1 1-.2-.2-.5-.3-.8-.3-.6 0-1.1.5-1.1 1.1 0 .4.2.8.6 1-.1.2-.1.5-.1.7 0 1.8 2.1 3.2 4.6 3.2s4.6-1.4 4.6-3.2c0-.2 0-.5-.1-.7.4-.2.6-.6.6-1zM6.5 10c0-.5.4-.9.9-.9s.9.4.9.9-.4.9-.9.9-.9-.4-.9-.9zm5.1 2.4c-.6.6-1.6.9-2.6.9s-2-.3-2.6-.9c-.1-.1-.1-.2 0-.3.1-.1.2-.1.3 0 .5.5 1.4.8 2.3.8s1.8-.3 2.3-.8c.1-.1.2-.1.3 0 .1.1.1.2 0 .3zm-.2-1.5c-.5 0-.9-.4-.9-.9s.4-.9.9-.9.9.4.9.9-.4.9-.9.9z" fill="white" />
                </svg>
                <span style={{ fontSize: 12, fontWeight: 600, color: t.textFaintAlt, fontFamily: t.fontMain, lineHeight: '16px' }}>
                  {card.sub} · ↑ {card.votes}
                </span>
              </div>
              <p style={{ fontSize: 14, lineHeight: '170%', color: t.textMuted, fontFamily: t.fontMain, margin: 0 }}>
                {card.quote}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

// ═══════════════════════════════════════════════
// FEATURE: FILE TOOLS
// ═══════════════════════════════════════════════
function FeatureFileTools() {
  const { mobile } = useBreakpoint()
  return (
    <section style={{
      width: '100%', height: mobile ? 'auto' : 560,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        display: 'flex', flexDirection: mobile ? 'column' : 'row',
        alignItems: mobile ? 'stretch' : 'center', gap: mobile ? 40 : 80,
        maxWidth: 1100, width: '100%', padding: mobile ? '64px 20px' : '0',
      }}>
        {/* Left mockup */}
        <div style={{
          flex: 1,
          maxWidth: mobile ? 360 : undefined,
          alignSelf: mobile ? 'center' : 'stretch',
          borderRadius: 14, overflow: 'hidden',
          boxShadow: '0 8px 32px var(--lp-shadow-light)',
        }}>
          <img src="/Landingpage/AllToolSection/AllToolsheader.png" alt="All Tools header" style={{ width: '100%', display: 'block' }} />
          <img src="/Landingpage/AllToolSection/allToolBody.png" alt="All Tools grid" style={{ width: '100%', display: 'block' }} />
        </div>

        {/* Right text */}
        <div style={{
          width: mobile ? '100%' : 420, flexShrink: 0,
          display: 'flex', flexDirection: 'column', gap: 28,
        }}>
          {/* Label + Heading group */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <span style={{ fontSize: 11, fontWeight: 500, letterSpacing: '0.12em', textTransform: 'uppercase' as const, color: t.accent, fontFamily: t.fontMain, lineHeight: '14px' }}>
              58+ file tools
            </span>
            <h2 style={{ fontSize: mobile ? 30 : 38, fontWeight: 700, letterSpacing: '-0.03em', lineHeight: '110%', color: t.text, fontFamily: t.fontMain, margin: 0 }}>
              Every file operation,{'\n'}already built in.
            </h2>
          </div>
          <p style={{ fontSize: 16, lineHeight: '165%', color: t.textMuted, margin: 0 }}>
            PDFs, Word docs, images, spreadsheets — convert, compress, OCR, watermark, split, merge. Everything you'd normally open a separate app for, already here.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {['PDF, Word, Excel, Image, Audio', 'No installs, no third-party logins', 'All results stay in your workspace'].map(text => (
              <div key={text} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, marginTop: 3 }}>
                  <circle cx="8" cy="8" r="7.5" stroke={t.accent} />
                  <path d="M5 8.5l2 2 4-4" stroke={t.accent} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span style={{ fontSize: 15, lineHeight: '150%', color: t.textMuted, fontFamily: t.fontMain }}>{text}</span>
              </div>
            ))}
          </div>
          {/* Logos strikethrough */}
          <div style={{ display: 'flex', flexDirection: 'column', marginTop: 8, gap: 10, maxWidth: mobile ? 340 : undefined }}>
            <div style={{ height: 52, overflow: 'hidden' }}>
              <img src="/Landingpage/AllToolSection/LogosRowStrikethrough@2x.png" alt="Replaced tools" style={{ width: '100%', maxWidth: mobile ? 340 : undefined, display: 'block' }} />
            </div>
            <p style={{ fontSize: 12, color: t.textFaint, lineHeight: '16px', letterSpacing: '0.01em', margin: 0 }}>
              Smallpdf · DocuSign · Canva · Dropbox · Otter AI · ilovePDF · Remove.bg — all replaced.
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}

// ═══════════════════════════════════════════════
// TESTIMONIALS
// ═══════════════════════════════════════════════
function Testimonials() {
  const { mobile } = useBreakpoint()
  const testimonialCards = [
    '/Landingpage/RealWorkRealResult/RWRR1.png',
    '/Landingpage/RealWorkRealResult/ RWRR2.png',
    '/Landingpage/RealWorkRealResult/RWRR3.png',
  ]

  return (
    <section style={{
      width: '100%', height: mobile ? 'auto' : 520,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        maxWidth: 1100, width: '100%', padding: mobile ? '64px 20px' : '0',
        display: 'flex', flexDirection: 'column', gap: 52,
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 11, fontWeight: 500, letterSpacing: '0.12em', textTransform: 'uppercase' as const, color: t.accent, fontFamily: t.fontMain, lineHeight: '14px', textAlign: 'center' }}>
            What people are saying
          </span>
          <h2 style={{ fontSize: mobile ? 28 : 42, fontWeight: 700, letterSpacing: '-0.03em', lineHeight: '110%', textAlign: 'center', color: t.text, fontFamily: t.fontMain, margin: 0 }}>
            Real work. Real results.
          </h2>
        </div>
        <div style={{ display: 'flex', flexDirection: mobile ? 'column' : 'row', gap: 20 }}>
          {testimonialCards.map((src, i) => (
            <div key={i} style={{ flex: 1, borderRadius: 14, overflow: 'hidden', maxWidth: mobile ? 340 : undefined, alignSelf: mobile ? 'center' : 'stretch' }}>
              <img src={src} alt="" style={{ width: '100%', display: 'block' }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

// ═══════════════════════════════════════════════
// FEATURE: E-SIGN & SEND
// ═══════════════════════════════════════════════
function FeatureESign() {
  const { mobile } = useBreakpoint()
  return (
    <section style={{
      width: '100%', height: mobile ? 'auto' : 540,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        display: 'flex', flexDirection: mobile ? 'column' : 'row',
        alignItems: mobile ? 'stretch' : 'center', gap: mobile ? 40 : 80,
        maxWidth: 1100, width: '100%', padding: mobile ? '64px 20px' : '0',
      }}>
        {/* Left text */}
        <div style={{
          width: mobile ? '100%' : 440, flexShrink: 0,
          display: 'flex', flexDirection: 'column', gap: 28,
        }}>
          {/* Label + Heading group */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <span style={{ fontSize: 11, fontWeight: 500, letterSpacing: '0.12em', textTransform: 'uppercase' as const, color: t.accent, fontFamily: t.fontMain, lineHeight: '14px' }}>
              E-sign & send
            </span>
            <h2 style={{ fontSize: mobile ? 30 : 38, fontWeight: 700, letterSpacing: '-0.03em', lineHeight: '110%', color: t.text, fontFamily: t.fontMain, margin: 0 }}>
              Sign it.{'\n'}Send it.{'\n'}Done.
            </h2>
          </div>
          <p style={{ fontSize: 16, lineHeight: '165%', color: t.textMuted, margin: 0 }}>
            Collect legally binding signatures from anyone — no DocuSign account, no back-and-forth. Send it, get it signed, and the completed doc saves itself.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <CheckItem>Legally binding in 180+ countries</CheckItem>
            <CheckItem>Send a signing link — no account required for recipients</CheckItem>
            <CheckItem>Signed copy auto-saved with full audit trail</CheckItem>
          </div>
        </div>

        {/* Right diagram */}
        <div style={{
          width: mobile ? '100%' : 500,
          maxWidth: mobile ? 340 : undefined,
          height: mobile ? 220 : 400,
          alignSelf: mobile ? 'center' : undefined,
          flexShrink: 0, position: 'relative',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <img src="/Landingpage/HerosScreens/Diagram@2x.png" alt="E-Sign flow diagram" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        </div>
      </div>
    </section>
  )
}

// ═══════════════════════════════════════════════
// FAQ SECTION
// ═══════════════════════════════════════════════
function FAQSection() {
  const { mobile } = useBreakpoint()
  const categories = [
    {
      label: 'Getting Started',
      items: [
        { q: "Is Corner actually free? What's the catch?", a: "Yes, genuinely free — no credit card, no trial, no expiry. The free plan gives you 7 AI conversations, 20 file operations, and e-sign for up to 5 documents per month. It stays free forever. You only upgrade if your volume grows and the limits start to feel tight. We'd rather you pay because Corner saved you time, not because we trapped you." },
        { q: 'How is Corner different from just using ChatGPT?', a: 'ChatGPT has a character limit and no document memory between sessions. You paste a chunk, get an answer, paste the next chunk, lose the context. Corner loads your entire document — up to 500 MB — and holds full context across your whole conversation. You can ask a follow-up about clause 14 after already discussing clause 3, and Corner remembers everything. No copy-pasting, no formatting loss.' },
        { q: 'Do I need to install anything?', a: 'No. Corner runs entirely in your browser. No desktop app required. The Gmail attachment picker works as a lightweight add-on if you want it, but the core product works out of the box.' },
      ],
    },
    {
      label: 'Documents & Files',
      items: [
        { q: 'What file types does Corner support?', a: 'PDFs, Word (.doc, .docx), Excel (.xls, .xlsx), images (JPG, PNG, TIFF, WEBP), audio files, and plain text. For AI conversations, PDFs and Word docs work best. The 58+ file tools cover a wider range of conversion and transformation operations.' },
        { q: 'Is there a file size limit?', a: 'Free plan: 25 MB per file. Pro and Business: up to 500 MB per file, with up to 10 files per conversation — so you can load a contract alongside its annexures, prior versions, and reference documents all at once.' },
        { q: 'Can Corner read scanned PDFs?', a: 'Yes. Corner includes built-in OCR that extracts text from scanned images and photographed documents. Upload the file, run OCR, and it becomes fully searchable and AI-readable. No third-party app needed.' },
      ],
    },
    {
      label: 'AI & Document Generation',
      items: [
        { q: 'Can Corner draft documents from scratch, or only analyze existing ones?', a: 'Both. Upload a document and Corner reviews, summarizes, flags clauses, or redlines it. Or describe what you need — "draft a 2-year NDA for a software contractor in New Jersey, include IP assignment and non-solicitation" — and Corner generates the full document. Generated documents are jurisdiction-adjusted so clauses are actually enforceable where you work, not just generic boilerplate.' },
        { q: 'Is the AI reading my whole document, or just the first few pages?', a: "The whole thing. Corner loads the full file into context — not just an excerpt. On a 50-page contract, its summaries, risk flags, and answers reflect the entire document. This is one of the core differences from pasting chunks into a general-purpose AI." },
        { q: 'Does Corner give legal advice?', a: "No — and it's important to be clear about that. Corner is a document tool, not a law firm. It can flag unusual clauses, summarize what a document says, identify what's missing, and generate standard templates — but it can't replace an attorney for complex matters. Think of it as a smart first pass that does the tedious work so your lawyer time is focused on what actually needs professional judgment." },
      ],
    },
    {
      label: 'Security & Privacy',
      items: [
        { q: 'Is my data private? Does Corner use my documents to train AI?', a: 'Your documents are never used to train AI models. Full stop. Corner is SOC 2 compliant with independently audited security practices. Files are processed to fulfill your request and not stored permanently beyond your workspace history. You can delete your data at any time.' },
      ],
    },
    {
      label: 'E-Sign & Sending',
      items: [
        { q: "Are Corner's e-signatures legally binding?", a: "Yes. Corner's e-signatures comply with the ESIGN Act (US), eIDAS (EU), and equivalent legislation in 180+ countries. Each signature comes with a full audit trail — timestamp, IP address, signer identity — and a certificate of completion. No DocuSign account needed for you or the people you're asking to sign." },
      ],
    },
    {
      label: 'Plans & Billing',
      items: [
        { q: 'What happens when I hit my free plan limits?', a: "Corner lets you know when you're approaching your limits. You won't lose access to your workspace or history — you just can't start new AI conversations or file operations until the month resets or you upgrade. We don't cut you off mid-conversation or delete your work." },
        { q: 'Can I cancel anytime?', a: "Yes. No contracts, no cancellation fees. Monthly plan: cancel before your next billing date, no further charges. Yearly plan: keep access through the end of your paid year. We don't hide the cancel button behind five menus and a phone call." },
      ],
    },
  ]

  return (
    <section id="faq" style={{
      width: '100%',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      padding: mobile ? '64px 20px' : '100px 0',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: mobile ? '100%' : 800, paddingBottom: 56, gap: 16 }}>
        <SectionBadge>Frequently Asked Questions</SectionBadge>
        <h2 style={{ fontSize: mobile ? 28 : 42, fontWeight: 700, letterSpacing: '-0.03em', lineHeight: mobile ? '115%' : '52px', textAlign: 'center', color: t.text, fontFamily: t.fontMain, margin: 0 }}>
          Everything you need to know
        </h2>
      </div>

      {/* Accordion */}
      <div style={{ width: mobile ? '100%' : 800, display: 'flex', flexDirection: 'column', gap: 48 }}>
        {categories.map(cat => (
          <FAQCategory key={cat.label} label={cat.label} items={cat.items} />
        ))}
      </div>
    </section>
  )
}

function FAQCategory({ label, items }: { label: string; items: { q: string; a: string }[] }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <div style={{
        display: 'flex', alignSelf: 'flex-start', marginBottom: 20,
        borderRadius: 100, padding: '6px 14px',
        backgroundColor: t.badgeBg,
      }}>
        <span style={{ fontSize: 11, fontWeight: 500, letterSpacing: '0.12em', textTransform: 'uppercase' as const, color: t.accent, fontFamily: t.fontMain, lineHeight: '14px' }}>
          {label}
        </span>
      </div>
      {items.map(item => (
        <FAQItem key={item.q} question={item.q} answer={item.a} />
      ))}
    </div>
  )
}

function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false)
  const { mobile } = useBreakpoint()
  return (
    <div style={{ display: 'flex', flexDirection: 'column', width: '100%', borderTop: `1px solid ${t.border}` }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          width: '100%', padding: '20px 0',
          background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left',
        }}
      >
        <span style={{ fontSize: 17, fontWeight: 600, color: t.text, fontFamily: t.fontMain, lineHeight: '140%' }}>{question}</span>
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0, marginLeft: 20, transform: open ? 'rotate(45deg)' : 'none', transition: 'transform 0.3s ease' }}>
          <line x1="10" y1="4" x2="10" y2="16" stroke={t.accent} strokeWidth="1.5" strokeLinecap="round" />
          <line x1="4" y1="10" x2="16" y2="10" stroke={t.accent} strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </button>
      <div style={{
        maxHeight: open ? 300 : 0,
        overflow: 'hidden',
        transition: 'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        }}>
        <div style={{
          display: 'flex', width: mobile ? '100%' : 760, paddingBottom: 20,
          opacity: open ? 1 : 0,
          transform: open ? 'translateY(0)' : 'translateY(-8px)',
          transition: 'opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1) 0.05s, transform 0.3s cubic-bezier(0.4, 0, 0.2, 1) 0.05s',
        }}>
          <p style={{ fontSize: 15, lineHeight: '165%', color: t.textBody, fontFamily: t.fontMain, margin: 0, maxWidth: 740 }}>{answer}</p>
        </div>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════
// FINAL CTA
// ═══════════════════════════════════════════════
function FinalCTA() {
  const { mobile } = useBreakpoint()
  return (
    <section style={{
      width: '100%', height: mobile ? 'auto' : 477,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      padding: mobile ? '64px 20px' : '80px 0',
    }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 32, maxWidth: 1100 }}>
        <SectionBadge>Get Started Today</SectionBadge>
        <h2 style={{ fontSize: mobile ? 28 : 44, fontWeight: 700, letterSpacing: '-0.03em', lineHeight: mobile ? '115%' : '54px', textAlign: 'center', color: t.text, fontFamily: t.fontMain, margin: 0, maxWidth: 1100 }}>
          The tools exist. The AI is ready. Your documents are waiting.
        </h2>
        <p style={{ fontSize: 16, color: t.textBody, textAlign: 'center', margin: 0, lineHeight: '20px' }}>
          Work is already stressful enough. Let Corner handle the paperwork so you can focus on what actually matters.
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', justifyContent: 'center', width: '100%' }}>
          <a href="/auth/register" style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: 10, padding: '14px 32px',
            backgroundColor: t.accentBtn, color: '#fff',
            fontSize: 16, fontWeight: 600, textDecoration: 'none', lineHeight: '20px',
            width: mobile ? '100%' : undefined,
            maxWidth: mobile ? 320 : undefined,
          }}>Get started free →</a>
          <a href="/pricing" style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: 10, padding: '14px 32px',
            border: `1px solid var(--lp-cta-border)`, color: t.text,
            fontSize: 16, fontWeight: 500, textDecoration: 'none', lineHeight: '20px',
            backgroundColor: 'transparent',
            width: mobile ? '100%' : undefined,
            maxWidth: mobile ? 320 : undefined,
          }}>See pricing</a>
        </div>
        <p style={{ fontSize: 13, color: t.textFaint, margin: 0, lineHeight: '16px' }}>
          Free forever on the basics · No credit card, no commitment
        </p>
      </div>
    </section>
  )
}

// ═══════════════════════════════════════════════
// THEME SWITCHER
// ═══════════════════════════════════════════════
type ThemeMode = 'auto' | 'light' | 'dark'

function ThemeSwitcher() {
  const [mode, setModeState] = useState<ThemeMode>(() => {
    if (typeof window === 'undefined') return 'auto'
    const stored = localStorage.getItem('corner:theme')
    if (stored === 'light' || stored === 'auto' || stored === 'dark') return stored
    return 'auto'
  })

  const setMode = (m: ThemeMode) => {
    setModeState(m)
    setTheme(m)
  }

  const modes: ThemeMode[] = ['auto', 'light', 'dark']
  const activeIdx = modes.indexOf(mode)
  const indicatorOffset = activeIdx * 30
  const iconColor = (m: ThemeMode) => mode === m ? '#F5F2EC' : '#F5F2EC59'

  return (
    <div style={{
      position: 'relative',
      display: 'inline-flex', alignItems: 'center', alignSelf: 'flex-start',
      borderRadius: 100, padding: 3, gap: 2, marginTop: 4, width: 'fit-content',
      backgroundColor: t.bgDark, border: '1px solid #F5F2EC14',
    }}>
      <div style={{
        position: 'absolute', top: 3, left: 3 + indicatorOffset,
        width: 28, height: 28, borderRadius: 100,
        backgroundColor: '#3A2E24', boxShadow: '#00000080 0px 1px 3px',
        transition: 'left 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        pointerEvents: 'none',
      }} />
      <button onClick={() => setMode('auto')} aria-label="System theme" style={{ position: 'relative', zIndex: 1, width: 28, height: 28, borderRadius: 100, border: 'none', cursor: 'pointer', backgroundColor: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <rect x="1" y="2" width="12" height="8" rx="1.5" stroke={iconColor('auto')} strokeWidth="1.2" fill="none" />
          <line x1="4" y1="12" x2="10" y2="12" stroke={iconColor('auto')} strokeWidth="1.2" strokeLinecap="round" />
        </svg>
      </button>
      <button onClick={() => setMode('light')} aria-label="Light theme" style={{ position: 'relative', zIndex: 1, width: 28, height: 28, borderRadius: 100, border: 'none', cursor: 'pointer', backgroundColor: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <circle cx="7" cy="7" r="3" stroke={iconColor('light')} strokeWidth="1.2" fill="none" />
          <line x1="7" y1="0.5" x2="7" y2="2" stroke={iconColor('light')} strokeWidth="1.2" strokeLinecap="round" />
          <line x1="7" y1="12" x2="7" y2="13.5" stroke={iconColor('light')} strokeWidth="1.2" strokeLinecap="round" />
          <line x1="0.5" y1="7" x2="2" y2="7" stroke={iconColor('light')} strokeWidth="1.2" strokeLinecap="round" />
          <line x1="12" y1="7" x2="13.5" y2="7" stroke={iconColor('light')} strokeWidth="1.2" strokeLinecap="round" />
        </svg>
      </button>
      <button onClick={() => setMode('dark')} aria-label="Dark theme" style={{ position: 'relative', zIndex: 1, width: 28, height: 28, borderRadius: 100, border: 'none', cursor: 'pointer', backgroundColor: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M12 8.5A5.5 5.5 0 1 1 5.5 2 4.5 4.5 0 0 0 12 8.5Z" stroke={iconColor('dark')} strokeWidth="1.2" fill="none" />
        </svg>
      </button>
    </div>
  )
}

// ═══════════════════════════════════════════════
// FOOTER
// ═══════════════════════════════════════════════
function Footer() {
  const { mobile } = useBreakpoint()
  return (
    <footer style={{
      width: '100%',
      padding: mobile ? '40px 20px 32px' : '56px 170px 40px',
      backgroundColor: t.bgDark,
      borderTop: '1px solid #F5F2EC14',
      fontFamily: t.fontMain,
    }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 48 }}>
        <div style={{ display: 'flex', flexDirection: mobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: 'flex-start', gap: mobile ? 32 : 0 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <CornerLogo color="#F5F2EC" size={22} gradId="footerLogoGrad" />
            <p style={{ fontSize: 13, lineHeight: '160%', color: '#F5F2EC59', maxWidth: 200, margin: 0 }}>
              Stop managing documents. Start finishing them.
            </p>
            <ThemeSwitcher />
          </div>
          <div style={{ display: 'flex', gap: mobile ? 28 : 72, flexWrap: mobile ? 'wrap' : 'nowrap', width: mobile ? '100%' : undefined }}>
            {[
              { title: 'Product', links: [
                { label: 'Features', href: '/features' },
                { label: 'Pricing', href: '/pricing' },
                { label: 'How it works', href: '/how-it-works' },
                { label: 'Changelog', href: null },
              ]},
              { title: 'Tools', links: [
                { label: 'PDF tools', href: null },
                { label: 'E-signature', href: null },
                { label: 'AI analysis', href: null },
                { label: 'File conversion', href: null },
              ]},
              { title: 'Company', links: [
                { label: 'About', href: null },
                { label: 'Blog', href: null },
                { label: 'Privacy', href: '/privacy' },
                { label: 'Terms', href: null },
              ]},
            ].map(col => (
              <div key={col.title} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' as const, color: '#F5F2EC40', lineHeight: '14px' }}>{col.title}</span>
                {col.links.map(link => (
                  link.href
                    ? <a key={link.label} href={link.href} style={{ fontSize: 13, color: '#F5F2EC80', textDecoration: 'none', lineHeight: '16px' }}>{link.label}</a>
                    : <span key={link.label} style={{ fontSize: 13, color: '#F5F2EC40', lineHeight: '16px', cursor: 'default' }}>{link.label}</span>
                ))}
              </div>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: mobile ? 'column' : 'row', gap: mobile ? 8 : 0, justifyContent: 'space-between', alignItems: mobile ? 'flex-start' : 'center', paddingTop: 24, borderTop: '1px solid #F5F2EC0F' }}>
          <span style={{ fontSize: 12, color: '#F5F2EC33', lineHeight: '16px' }}>© 2026 Corner. All rights reserved.</span>
          <span style={{ fontSize: 12, color: '#F5F2EC33', lineHeight: '16px' }}>trycorner.co</span>
        </div>
      </div>
    </footer>
  )
}

// ═══════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════
export default function LandingPage() {
  useEffect(() => {
    const hash = window.location.hash.replace('#', '')
    if (hash) {
      // Small delay to let the page render before scrolling
      setTimeout(() => {
        document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth' })
      }, 100)
    }
  }, [])

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      backgroundColor: t.bg, fontFamily: t.fontMain, minHeight: '100vh',
      width: '100%', overflowX: 'hidden',
    }}>
      <Navbar />
      <ScrollReveal style={{ width: '100%' }}><Hero /></ScrollReveal>
      <ScrollReveal style={{ width: '100%' }} delay={0.15}><HeroMockup /></ScrollReveal>
      <ScrollReveal style={{ width: '100%' }}><SocialProof /></ScrollReveal>
      <ScrollReveal style={{ width: '100%' }}><HowItWorks /></ScrollReveal>
      <ScrollReveal style={{ width: '100%' }}><WorkflowCards /></ScrollReveal>
      <ScrollReveal style={{ width: '100%' }}><FeatureAIAnalysis /></ScrollReveal>
      <ScrollReveal style={{ width: '100%' }}><FeatureDocGen /></ScrollReveal>
      <ScrollReveal style={{ width: '100%' }}><FeatureCollaboration /></ScrollReveal>
      <ScrollReveal style={{ width: '100%' }}><RedditPain /></ScrollReveal>
      <ScrollReveal style={{ width: '100%' }}><FeatureFileTools /></ScrollReveal>
      <ScrollReveal style={{ width: '100%' }}><Testimonials /></ScrollReveal>
      <ScrollReveal style={{ width: '100%' }}><FeatureESign /></ScrollReveal>
      <ScrollReveal style={{ width: '100%' }}><FAQSection /></ScrollReveal>
      <ScrollReveal style={{ width: '100%' }}><FinalCTA /></ScrollReveal>
      <ScrollReveal style={{ width: '100%' }}><Footer /></ScrollReveal>
    </div>
  )
}
